import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { animated, useTransition, config } from '@react-spring/web';
import { SvgSprite } from 'modules/svgSprite';
import { toHtmlContent } from 'modules/helpers/strings';
import { useSessionState } from 'hooks/storage';
import { ColorAdvice4, BorderRadiusSmall } from 'autogen/design-tokens/tokens';
import dynamic from 'helpers/dynamic';
import { BottomWrapper, IconWrapper, ImageWrapper, TopWrapper, Wrapper, TextDesktop, TextMobile } from './styles';
import { useChatStore } from '../ChatStore';

const HeroCtaCurve = dynamic(() => import('icons/HeroCtaCurve.svg?react'));

const AnimatedWrapper = animated<any>(Wrapper);

const CloseButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 24px;
    background-color: ${ColorAdvice4};
    border-top-left-radius: ${BorderRadiusSmall};
    cursor: pointer;
`;

const Closebutton = styled(SvgSprite)`
    width: 14px;
    height: 14px;
    background-color: ${ColorAdvice4};
`;

export const BottomWrapperContainer = styled.div`
    display: flex;
`;

export const BottomWrapperCurve = styled(HeroCtaCurve)`
    width: 21.66px;
    height: 62px;
    margin-left: -2px; // added to prevent spacing between curve and content
    
    path {
        fill: ${ColorAdvice4};
    }
`;

const AnimatedHeroCta = () => {
    const desktopTitle = useChatStore((state) => state.desktopTitle) ?? '';
    const mobileTitle = useChatStore((state) => state.mobileTitle) ?? '';
    const isChatNudgingEnabled = useChatStore((state) => state.isChatNudgingEnabled);
    const isOnline = useChatStore((state) => state.isOnline);

    const [open, setOpen] = useState(false);
    const [activeSession, setActiveSession] = useSessionState<boolean>('KindlyCTASession', false);

    const canOpen = useCallback(
        () => open === false && isChatNudgingEnabled && isOnline && activeSession === false,
        [activeSession, isChatNudgingEnabled, isOnline, open]
    );

    const delayToShowHeroCta = 3000;
    const heroCtaVisibleTime = delayToShowHeroCta + 10000;

    const ScheduleDelayedOpen = useCallback(
        () =>
            setTimeout(() => {
                setActiveSession(true);
                setOpen(true);
            }, delayToShowHeroCta),
        [setActiveSession]
    );

    const wrapperTransitions = useTransition(open, {
        from: { opacity: 0, transform: 'translate3d(340px, 0px, 0px)' },
        enter: { opacity: 1, transform: 'translate3d(0px, 0px, 0px)' },
        leave: { opacity: 0, transform: 'translate3d(340px, 0px, 0px)' },
        config: config.slow,
        reverse: open,
        onRest: () => {
            setTimeout(() => {
                setOpen(false);
            }, heroCtaVisibleTime);
        }
    });

    const onClose = () => {
        setOpen(false);
    };

    // Handles setting up the different timers for opening the hero depending on the page.
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        if (canOpen()) {
            timer = ScheduleDelayedOpen();
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [canOpen, setActiveSession, ScheduleDelayedOpen]);

    if (!isChatNudgingEnabled) return null;

    return wrapperTransitions(
        (styles, item) =>
            item && (
                <AnimatedWrapper style={{ ...styles }} sidePadding alternateDesign>
                    <TopWrapper showCloseButton alternateDesign>
                        <ImageWrapper>
                            <CloseButtonWrapper onClick={onClose}>
                                <Closebutton sprite="SmallX" />
                            </CloseButtonWrapper>
                            <IconWrapper src="/images/hero-cta_expert-1.png" loading="lazy" />
                            <IconWrapper src="/images/hero-cta_expert-2.png" loading="lazy" />
                            <IconWrapper src="/images/hero-cta_expert-3.png" loading="lazy" />
                        </ImageWrapper>
                    </TopWrapper>
                    <BottomWrapperContainer>
                        <BottomWrapper alternateDesign>
                            <TextDesktop alternateDesign>{toHtmlContent(desktopTitle)}</TextDesktop>
                            <TextMobile alternateDesign>{toHtmlContent(mobileTitle)}</TextMobile>
                        </BottomWrapper>
                        <BottomWrapperCurve />
                    </BottomWrapperContainer>
                </AnimatedWrapper>
            )
    );
};

export default AnimatedHeroCta;
